import { Injectable, signal } from '@angular/core';

export type NoticeKind = 'success' | 'warning' | 'error';

export interface Notice {
  /** A new notice gets a new id, so the bar slides in again even when the text is the same as the last one. */
  id: number;
  kind: NoticeKind;
  text: string;
}

/** Long enough to read two short sentences; the bar is closable, so nobody has to wait for it either. */
const VISIBLE_MS = 5000;

/**
 * The one message bar of the app: what just happened after an action, as a short line at the bottom.
 *
 * It lives in the service rather than in a screen, so a screen can navigate and the next screen shows the
 * message: "Je vraag is verstuurd" is read on the list the member lands on, not on the form that is gone.
 * Only one notice at a time; a new one replaces the old one, because a stack of old news is noise.
 */
@Injectable({ providedIn: 'root' })
export class NoticeService {
  private readonly current = signal<Notice | null>(null);
  private nextId = 1;
  private hideTimer: ReturnType<typeof setTimeout> | null = null;

  readonly notice = this.current.asReadonly();

  showSuccess(text: string): void {
    this.show('success', text);
  }

  /** Done, but something next to it did not work: the change is saved, the follow-up (a mail) is not. */
  showWarning(text: string): void {
    this.show('warning', text);
  }

  showError(text: string): void {
    this.show('error', text);
  }

  dismiss(): void {
    this.clearTimer();
    this.current.set(null);
  }

  /** While the pointer or the focus is on the bar it stays: nobody should lose a message they are reading. */
  hold(): void {
    this.clearTimer();
  }

  release(): void {
    if (this.current() !== null) {
      this.startTimer();
    }
  }

  private show(kind: NoticeKind, text: string): void {
    this.current.set({ id: this.nextId++, kind, text });
    this.startTimer();
  }

  private startTimer(): void {
    this.clearTimer();
    this.hideTimer = setTimeout(() => this.dismiss(), VISIBLE_MS);
  }

  private clearTimer(): void {
    if (this.hideTimer !== null) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
  }
}
